
import { ColorPreset } from '@shared/components/generic-colorpreset/generic-colorpreset.component';

/** Convert a hex string ("#RRGGBB") to { h, s, l } (all in [0…1]) */
export function hexToHSL(hex: string): { h: number; s: number; l: number } {
    const c = hex.replace(/^#/, '');
    const r = parseInt(c.slice(0, 2), 16) / 255;
    const g = parseInt(c.slice(2, 4), 16) / 255;
    const b = parseInt(c.slice(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return { h, s, l };
}

/** Convert an HSL triple (all in [0…1]) back to hex "#RRGGBB" */
export function hslToHex(h: number, s: number, l: number): string {
    function hue2rgb(p: number, q: number, t: number) {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    }

    let r: number, g: number, b: number;
    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const q = l < 0.5
            ? l * (1 + s)
            : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (x: number) => {
        const hex = Math.round(x * 255).toString(16).padStart(2, '0');
        return hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Given a background hex, returns a "smart" text color
 * that's the same hue but high-contrast lightness.
 */
export function getTintedContrastColor(bgHex: string): string {
    const { h, s, l } = hexToHSL(bgHex);
    let textL: number;

    // choose text lightness: dark text if bg is light, light text if bg is dark
    if (l > 0.6) {
        textL = Math.max(0.3, l / 4);
    } else {
        textL = Math.min(0.95, l + 0.5);
    }

    return hslToHex(h, s, textL);
}

/**
 * Creates a bleached/greyed out version of a hex color by blending with grey
 */
export function getBleachedColor(color: string): string {
    // Convert hex to RGB, then blend with grey
    if (!color || !color.startsWith('#')) return color;

    const hex = color.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Blend with grey (grey, grey, grey) at blendFactor opacity
    const blendFactor = 0.7;
    const grey = 250;

    const newR = Math.round(r * (1 - blendFactor) + grey * blendFactor);
    const newG = Math.round(g * (1 - blendFactor) + grey * blendFactor);
    const newB = Math.round(b * (1 - blendFactor) + grey * blendFactor);

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

/**
 * Predefined color presets with matching text colors
 */
export const COLOR_PRESETS: ColorPreset[] = [
    { tagColor: '#e6e6e6', tagTextColor: '#3d3d3d', label: 'Light Gray', value: 'light-gray' },
    { tagColor: '#ffcfc9', tagTextColor: '#b10202', label: 'Light Red', value: 'light-red' },
    { tagColor: '#ffc8aa', tagTextColor: '#753800', label: 'Light Orange', value: 'light-orange' },
    { tagColor: '#ffe5a0', tagTextColor: '#473822', label: 'Light Yellow', value: 'light-yellow' },
    { tagColor: '#d4edbc', tagTextColor: '#11734b', label: 'Light Green', value: 'light-green' },
    { tagColor: '#bfe1f6', tagTextColor: '#0a53a8', label: 'Light Blue', value: 'light-blue' },
    { tagColor: '#c6dbe1', tagTextColor: '#215a6c', label: 'Light Teal', value: 'light-teal' },
    { tagColor: '#e6cff2', tagTextColor: '#5a3286', label: 'Light Purple', value: 'light-purple' },
    { tagColor: '#3d3d3d', tagTextColor: '#e6e6e6', label: 'Dark Gray', value: 'dark-gray' },
    { tagColor: '#b10202', tagTextColor: '#ffcfc9', label: 'Dark Red', value: 'dark-red' },
    { tagColor: '#753800', tagTextColor: '#ffc8aa', label: 'Dark Orange', value: 'dark-orange' },
    { tagColor: '#473822', tagTextColor: '#ffe5a0', label: 'Dark Yellow', value: 'dark-yellow' },
    { tagColor: '#11734b', tagTextColor: '#d4edbc', label: 'Dark Green', value: 'dark-green' },
    { tagColor: '#0a53a8', tagTextColor: '#bfe1f6', label: 'Dark Blue', value: 'dark-blue' },
    { tagColor: '#215a6c', tagTextColor: '#c6dbe1', label: 'Dark Teal', value: 'dark-teal' },
    { tagColor: '#5a3286', tagTextColor: '#e6cff2', label: 'Dark Purple', value: 'dark-purple' },
];
